// Purpose: shared positioning hook for portaled popovers (MultiSelect,
// SearchableSelect). Returns viewport-anchored coords + width for a
// popover that's rendering through createPortal into document.body —
// so the popover escapes its parent's stacking context (Commit 8b.12.1
// fix for the form-fields-painted-over-popover bug) but still lines up
// with its trigger button.
//
// Recomputes on scroll + resize while open so the popover tracks the
// trigger if the page moves underneath. Stops listening on close.

import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

export interface PopoverPosition {
  /** position: absolute, top in document coordinates (window.scrollY-aware). */
  top: number;
  /** position: absolute, left in document coordinates. */
  left: number;
  /** Width of the trigger; popovers can use or ignore this. */
  width: number;
}

export function usePopoverPosition(
  triggerRef: RefObject<HTMLElement | null>,
  open: boolean
): PopoverPosition | null {
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const compute = (): void => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Use document coordinates (rect + scroll) so position: absolute
      // on the body-portaled popover lines up regardless of scroll.
      setPos({
        top: r.bottom + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
      });
    };
    compute();
    // Capture-phase listeners catch scroll on any ancestor that has its
    // own scrollbar (e.g. <main className="overflow-y-auto">), not just
    // window. Resize is window-only.
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, triggerRef]);

  return pos;
}
